// Exposure logic: judges player's settings vs. room target.

// Compute live visual preview fx from current settings (no grading side effects).
// Used to make the viewfinder react in real time as the student turns dials.
export function computeLivePreviewFx({ room, aperture, shutter, iso }) {
    const playerEV = aperture.stops + shutter.stops - iso.stops;
    const diff = playerEV - room.targetEV;

    // Smooth exposure curve: each stop of diff halves/doubles brightness.
    // diff > 0 = OVER-exposed → brighter frame. diff < 0 = UNDER-exposed → darker frame.
    // (clamped so the scene stays vaguely readable at extremes)
    const brightness = clamp(Math.pow(2, diff / 1.5), 0.15, 3.0);

    // Motion blur — proportional to how far under the required shutter speed
    let motionBlur = 0;
    if (room.motionRequirement) {
        const deficit = room.motionRequirement - shutter.stops;
        if (deficit > 0) motionBlur = Math.min(12, deficit * 2);
    } else if (shutter.stops <= 2) {
        motionBlur = (3 - shutter.stops) * 1.5;  // slight handshake blur for very slow
    }

    // Depth-of-field blur on the background — wider aperture = more blur
    const bgBlur = Math.max(0, 8 - aperture.stops);

    // Noise from ISO — soft curve so a correct-ISO frame isn't washed out.
    // stops 0 -> 0, 2 -> ~0.08, 4 -> ~0.38, 6 -> 0.9
    const noise = clamp(Math.pow(Math.max(0, iso.stops) / 6, 2.2) * 0.9, 0, 0.9);

    // Tint: warm red for under-exposed, hot white for over-exposed
    let tint = null;
    if (diff <= -2) tint = "under";
    else if (diff >= 2) tint = "over";

    // Human-readable EV label for the HUD badge
    let evLabel;
    if (diff <= -2) evLabel = `${diff} EV`;
    else if (diff === -1) evLabel = "-1 EV";
    else if (diff === 0) evLabel = "OK EV";
    else if (diff === 1) evLabel = "+1 EV";
    else evLabel = `+${diff} EV`;

    const evTone = diff === 0 ? "ok" : Math.abs(diff) === 1 ? "warn" : "bad";

    // In-range check for the Practice ghost reticle — ALL room constraints satisfied
    const motionOk = room.motionRequirement ? shutter.stops >= room.motionRequirement : true;
    const dofOk = room.dofRequirement?.type === "shallow"
        ? aperture.stops <= room.dofRequirement.maxApertureStops
        : true;
    const noiseOk = room.noiseCeilingStops === undefined
        ? iso.stops < 6  // general: iso 6400 always ruins
        : iso.stops <= room.noiseCeilingStops;
    const inRange = diff === 0 && motionOk && dofOk && noiseOk;

    // Excess slowness of the shutter — drives the "light trails" reticle overlay.
    // Shutter option stops jump 0/4/6/8/10, so we threshold at <=4 so both "1 s"
    // AND "1/15" trigger visible handshake trails in non-motion rooms.
    let shutterExcess = 0;
    if (room.motionRequirement && shutter.stops < room.motionRequirement) {
        shutterExcess = room.motionRequirement - shutter.stops;
    } else if (shutter.stops <= 4) {
        shutterExcess = 5 - shutter.stops;
    }

    return { brightness, motionBlur, bgBlur, noise, tint, evLabel, evTone, evDiff: diff,
             inRange, motionOk, dofOk, noiseOk, shutterExcess };
}

function clamp(v, lo, hi) {
    return Math.max(lo, Math.min(hi, v));
}

export function evaluateShot({ room, aperture, shutter, iso }) {
    const playerEV = aperture.stops + shutter.stops - iso.stops;
    const diff = playerEV - room.targetEV;

    const flaws = [];
    let exposure = "correct";
    if (diff <= -2) { exposure = "very_under"; flaws.push("UNDER_EXPOSED"); }
    else if (diff === -1 && room.evTolerance < 1) { exposure = "under"; flaws.push("UNDER_EXPOSED"); }
    else if (diff >= 2)  { exposure = "very_over";  flaws.push("OVER_EXPOSED"); }
    else if (diff === 1 && room.evTolerance < 1) { exposure = "over"; flaws.push("OVER_EXPOSED"); }
    // within ±tolerance => correct

    // Motion blur check
    if (room.motionRequirement && shutter.stops < room.motionRequirement) {
        flaws.push("MOTION_BLUR");
    }

    // DOF check
    if (room.dofRequirement?.type === "shallow") {
        if (aperture.stops > room.dofRequirement.maxApertureStops) {
            flaws.push("DOF_TOO_DEEP");
        }
    }

    // Noise check (universal: >= 6400 always ruins; per-room ceiling for boss)
    if (iso.stops >= 6) flaws.push("NOISE_HEAVY");
    else if (iso.stops >= 4 && room.noiseCeilingStops !== undefined && iso.stops > room.noiseCeilingStops) {
        flaws.push("NOISE_TRIGGER");
    }

    const grade = computeGrade(flaws, Math.abs(diff));
    const success = grade === "A+" || grade === "A";

    return {
        playerEV,
        diff,
        exposure,
        flaws,
        grade,
        success,
        // Visual fx hints for the viewfinder/polaroid
        fx: {
            brightness: diff >= 2 ? 2.2 : diff <= -2 ? 0.15 : diff === 1 ? 1.5 : diff === -1 ? 0.6 : 1,
            blur: flaws.includes("MOTION_BLUR") ? 6 : 0,
            noise: iso.stops >= 4 ? Math.min(1, (iso.stops - 2) / 5) : 0,
            dofBlur: flaws.includes("DOF_TOO_DEEP") ? 0 : (room.dofRequirement?.type === "shallow" ? 3 : 0),
        },
    };
}

function computeGrade(flaws, expDiff) {
    if (flaws.length === 0 && expDiff === 0) return "A+";
    if (flaws.length === 0) return "A";
    if (flaws.length === 1 && !["NOISE_HEAVY", "OVER_EXPOSED", "VERY_UNDER"].some(f => flaws.includes(f))) return "B";
    if (flaws.includes("NOISE_HEAVY") || flaws.includes("OVER_EXPOSED")) return "F";
    return "C";
}

// Compute live meter readings even before pressing shutter.
// Noise curve is aligned with computeLivePreviewFx so the sidebar meter matches
// the viewfinder overlay the student is seeing. Handshake blur threshold matches
// the shutter-trail overlay so meter and visuals agree.
export function computeMeters({ shutter, iso, room }) {
    const noise = clamp(Math.pow(Math.max(0, iso.stops) / 6, 2.2) * 0.9, 0, 0.9);
    let blur = 0;
    if (room.motionRequirement) {
        const deficit = room.motionRequirement - shutter.stops;
        if (deficit > 0) blur = Math.min(1, deficit / 6);
    } else if (shutter.stops <= 4) {
        blur = (5 - shutter.stops) / 6;
    }
    return {
        noise,
        blur:  Math.max(0, Math.min(1, blur)),
    };
}
